/**
 * Quiz Repository - Cache layer for quiz state persistence
 * 
 * Implements localStorage for offline-first caching with Supabase DB sync.
 * 
 * Contract:
 * - loadQuizDraft(): retrieves saved quiz state (DB first for auth users, then localStorage)
 * - saveQuizDraft(): persists quiz state (local cache + debounced sync to DB)
 * - clearQuizDraft(): removes quiz draft from storage
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';

const QUIZ_DRAFT_KEY = 'quiz_draft_v1';
const SYNC_DEBOUNCE_MS = 2000; // 2 seconds debounce for DB sync

export interface QuizStepAnswer {
  [key: string]: unknown;
}

export interface QuizState {
  currentStep: number;
  totalSteps: number;
  answers: Record<number, QuizStepAnswer | string>;
  updatedAt: number;
  startedAt?: string;
  processingStatus?: 'idle' | 'loading' | 'success' | 'error';
}

const DEFAULT_QUIZ_STATE: QuizState = {
  currentStep: 0,
  totalSteps: 9,
  answers: {},
  updatedAt: Date.now(),
};

// Debounce timer for DB sync
let syncTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Get user-specific storage key
 */
function getStorageKey(userId?: string): string {
  const suffix = userId || 'anonymous';
  return `${QUIZ_DRAFT_KEY}_${suffix}`;
}

/**
 * Load quiz draft from storage
 * For authenticated users: try DB first, then localStorage
 * For anonymous users: localStorage only
 */
export async function loadQuizDraft(userId?: string): Promise<QuizState | null> {
  try {
    // For authenticated users, try DB first
    if (userId) {
      const { data: dbDraft, error } = await supabase
        .from('quiz_drafts')
        .select('answers, current_step, total_steps, started_at, updated_at')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (!error && dbDraft) {
        const state: QuizState = {
          currentStep: dbDraft.current_step,
          totalSteps: dbDraft.total_steps,
          answers: (dbDraft.answers as Record<number, QuizStepAnswer | string>) || {},
          updatedAt: new Date(dbDraft.updated_at).getTime(),
          startedAt: dbDraft.started_at,
        };
        
        logger.debug('Quiz draft loaded from DB', { 
          data: { currentStep: state.currentStep, answersCount: Object.keys(state.answers).length },
          allowList: ['currentStep', 'answersCount'],
        });
        
        // Also sync to localStorage for offline access
        const key = getStorageKey(userId);
        localStorage.setItem(key, JSON.stringify(state));
        
        return state;
      }
    }
    
    // Fallback to localStorage
    const key = getStorageKey(userId);
    const stored = localStorage.getItem(key);
    
    if (!stored) {
      logger.debug('No quiz draft found in storage');
      return null;
    }
    
    const parsed = JSON.parse(stored) as QuizState;
    logger.debug('Quiz draft loaded from localStorage', { 
      data: { currentStep: parsed.currentStep, answersCount: Object.keys(parsed.answers).length },
      allowList: ['currentStep', 'answersCount'],
    });
    
    return parsed;
  } catch (error) {
    logger.error('Failed to load quiz draft', { error });
    return null;
  }
}

/**
 * Sync quiz draft to Supabase DB
 */
async function syncToDatabase(state: QuizState, userId: string): Promise<void> {
  try {
    // Check if record exists
    const { data: existing } = await supabase
      .from('quiz_drafts')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    
    // Cast answers to JSON-compatible type
    const answersJson = JSON.parse(JSON.stringify(state.answers));
    
    if (existing) {
      // Update existing record
      const { error } = await supabase
        .from('quiz_drafts')
        .update({
          answers: answersJson,
          current_step: state.currentStep,
          total_steps: state.totalSteps,
        })
        .eq('user_id', userId);
      
      if (error) {
        logger.error('Failed to update quiz draft in DB', { error });
      } else {
        logger.debug('Quiz draft updated in DB', {
          data: { currentStep: state.currentStep },
          allowList: ['currentStep'],
        });
      }
    } else {
      // Insert new record
      const { error } = await supabase
        .from('quiz_drafts')
        .insert([{
          user_id: userId,
          answers: answersJson,
          current_step: state.currentStep,
          total_steps: state.totalSteps,
          started_at: state.startedAt || new Date().toISOString(),
        }]);
      
      if (error) {
        logger.error('Failed to insert quiz draft to DB', { error });
      } else {
        logger.debug('Quiz draft inserted to DB', {
          data: { currentStep: state.currentStep },
          allowList: ['currentStep'],
        });
      }
    }
  } catch (error) {
    logger.error('Exception syncing quiz draft to DB', { error });
  }
}

/**
 * Save quiz draft to storage
 * Saves to localStorage immediately, syncs to DB with debounce for auth users
 */
export async function saveQuizDraft(
  state: Partial<QuizState>,
  userId?: string
): Promise<void> {
  try {
    const key = getStorageKey(userId);
    
    // Merge with existing state
    const existing = await loadQuizDraft(userId);
    const merged: QuizState = {
      ...DEFAULT_QUIZ_STATE,
      ...existing,
      ...state,
      answers: {
        ...(existing?.answers || {}),
        ...(state.answers || {}),
      },
      updatedAt: Date.now(),
      startedAt: existing?.startedAt || state.startedAt || new Date().toISOString(),
    };
    
    // Save to localStorage immediately
    localStorage.setItem(key, JSON.stringify(merged));
    
    logger.debug('Quiz draft saved to localStorage', {
      data: { currentStep: merged.currentStep, answersCount: Object.keys(merged.answers).length },
      allowList: ['currentStep', 'answersCount'],
    });
    
    // For authenticated users, sync to DB with debounce
    if (userId) {
      // Clear existing timer
      if (syncTimer) {
        clearTimeout(syncTimer);
      }
      
      // Set new timer
      syncTimer = setTimeout(() => {
        syncToDatabase(merged, userId);
        syncTimer = null;
      }, SYNC_DEBOUNCE_MS);
    }
  } catch (error) {
    logger.error('Failed to save quiz draft', { error });
    throw error;
  }
}

/**
 * Force immediate sync to DB (use before submitting)
 */
export async function flushQuizDraft(userId?: string): Promise<void> {
  if (!userId) return;
  
  // Clear any pending debounce
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  
  // Load current state and sync
  const state = await loadQuizDraft(userId);
  if (state) {
    await syncToDatabase(state, userId);
  }
}

/**
 * Clear quiz draft from storage
 * Removes from both localStorage and DB
 */
export async function clearQuizDraft(userId?: string): Promise<void> {
  try {
    // Clear pending sync
    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
    }
    
    // Clear localStorage
    const key = getStorageKey(userId);
    localStorage.removeItem(key);
    
    // Clear from DB for authenticated users
    if (userId) {
      const { error } = await supabase
        .from('quiz_drafts')
        .delete()
        .eq('user_id', userId);
      
      if (error) {
        logger.error('Failed to clear quiz draft from DB', { error });
      }
    }
    
    logger.debug('Quiz draft cleared from storage');
  } catch (error) {
    logger.error('Failed to clear quiz draft', { error });
    throw error;
  }
}

/**
 * Hook-friendly wrapper to get initial quiz state
 */
export function getDefaultQuizState(): QuizState {
  return { ...DEFAULT_QUIZ_STATE };
}
